import React, { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PageTitle from "../../layouts/PageTitle";
import { getAdminSessionsApi } from "../../../services/EarningsService";

const STATUS_MAP = {
  active: { label: "Active", cls: "badge-info" },
  ended: { label: "Ended", cls: "badge-success" },
  insufficient_balance: { label: "Low Balance", cls: "badge-danger" },
};

const formatDate = (v) => {
  if (!v) return "-";
  return new Date(v).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const formatDuration = (seconds) => {
  if (!seconds) return "-";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const AllSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState(20);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [filters, setFilters] = useState({ search: "", status: "", sessionType: "", dateFrom: null, dateTo: null });
  const [applied, setApplied] = useState({});

  const fetchSessions = async (params) => {
    try {
      setLoading(true);
      const res = await getAdminSessionsApi(params);
      const d = res?.data;
      setSessions(Array.isArray(d?.data) ? d.data : []);
      setTotal(Number(d?.pagination?.total) || 0);
      setTotalPages(Number(d?.pagination?.totalPages) || 1);
    } catch {
      setSessions([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions({ page, limit: sort, ...applied });
  }, [page, sort, applied]);

  const handleApply = () => {
    setPage(1);
    setApplied({
      ...filters,
      dateFrom: filters.dateFrom ? filters.dateFrom.toISOString().split("T")[0] : "",
      dateTo: filters.dateTo ? filters.dateTo.toISOString().split("T")[0] : "",
    });
  };
  const handleReset = () => {
    const empty = { search: "", status: "", sessionType: "", dateFrom: null, dateTo: null };
    setFilters(empty); setApplied({}); setPage(1);
  };

  const paggination = Array(totalPages).fill(null).map((_, i) => i + 1);
  const showingFrom = total > 0 ? (page - 1) * sort + 1 : 0;
  const showingTo   = total > 0 ? Math.min(page * sort, total) : 0;

  return (
    <>
      <PageTitle activeMenu="All Sessions" motherMenu="Sessions" />

      {/* ── Summary Cards ── */}
      <div className="row mb-4">
        {[
          { label: "Total Sessions", value: total, icon: "fa-list", color: "primary" },
        ].map((c) => (
          <div className="col-md-3" key={c.label}>
            <div className="card">
              <div className="card-body d-flex align-items-center gap-3">
                <div className={`rounded-circle d-flex align-items-center justify-content-center bg-${c.color} text-white`}
                  style={{ width: 48, height: 48, fontSize: 18 }}>
                  <i className={`fa ${c.icon}`} />
                </div>
                <div>
                  <div className="text-muted small">{c.label}</div>
                  <div className="fs-4 fw-bold">{c.value}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="card mb-4">
        <div className="card-header"><h4 className="card-title mb-0">Search & Filter</h4></div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <input type="text" className="form-control" placeholder="Customer, consultant or session ID"
                value={filters.search} onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))} />
            </div>
            <div className="col-md-2">
              <select className="form-control" value={filters.status}
                onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}>
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="ended">Ended</option>
                <option value="insufficient_balance">Low Balance</option>
              </select>
            </div>
            <div className="col-md-2">
              <select className="form-control" value={filters.sessionType}
                onChange={(e) => setFilters((p) => ({ ...p, sessionType: e.target.value }))}>
                <option value="">All Types</option>
                <option value="call">Call</option>
                <option value="chat">Chat</option>
              </select>
            </div>
            <div className="col-md-2">
              <DatePicker
                selected={filters.dateFrom}
                onChange={(date) => setFilters((p) => ({ ...p, dateFrom: date }))}
                selectsStart
                startDate={filters.dateFrom}
                endDate={filters.dateTo}
                maxDate={filters.dateTo || new Date()}
                placeholderText="From date"
                dateFormat="dd/MM/yyyy"
                className="form-control"
                wrapperClassName="w-100"
                isClearable
              />
            </div>
            <div className="col-md-2">
              <DatePicker
                selected={filters.dateTo}
                onChange={(date) => setFilters((p) => ({ ...p, dateTo: date }))}
                selectsEnd
                startDate={filters.dateFrom}
                endDate={filters.dateTo}
                minDate={filters.dateFrom}
                maxDate={new Date()}
                placeholderText="To date"
                dateFormat="dd/MM/yyyy"
                className="form-control"
                wrapperClassName="w-100"
                isClearable
              />
            </div>
          </div>
          <div className="mt-3 d-flex gap-2">
            <button className="btn btn-primary btn-sm" onClick={handleApply}>Apply</button>
            <button className="btn btn-outline-secondary btn-sm" onClick={handleReset}>Reset</button>
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h4 className="card-title mb-0">Sessions</h4>
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small">Show</span>
            <Dropdown>
              <Dropdown.Toggle as="div" className="search-drop-btn" style={{ cursor: "pointer" }}>{sort}</Dropdown.Toggle>
              <Dropdown.Menu>
                {[10, 20, 30, 50].map((n) => (
                  <Dropdown.Item key={n} onClick={() => { setSort(n); setPage(1); }}>{n}</Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <span className="text-muted small">entries</span>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Customer</th>
                  <th>Consultant</th>
                  <th>Type</th>
                  <th>Duration</th>
                  <th>Rate/min</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Started At</th>
                  <th>Ended At</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={10} className="text-center py-5 text-muted">Loading sessions...</td></tr>
                )}
                {!loading && sessions.length === 0 && (
                  <tr><td colSpan={10} className="text-center py-5 text-muted">No sessions found.</td></tr>
                )}
                {!loading && sessions.map((s, i) => {
                  const st = STATUS_MAP[s.status] || { label: s.status, cls: "badge-secondary" };
                  return (
                    <tr key={s.id}>
                      <td className="text-muted small">{showingFrom + i}</td>
                      <td>
                        <div className="fw-semibold" style={{ textTransform: "capitalize" }}>{s.customerName}</div>
                        <div className="text-muted small">{s.customerEmail}</div>
                      </td>
                      <td style={{ textTransform: "capitalize" }}>{s.consultantName}</td>
                      <td>
                        <span className={`badge light ${s.sessionType === "call" ? "badge-primary" : "badge-warning"}`}>
                          <i className={`fa fa-${s.sessionType === "call" ? "phone" : "comments"} me-1`} />
                          {s.sessionType === "call" ? "Call" : "Chat"}
                        </span>
                      </td>
                      <td>{formatDuration(s.consumedSeconds)}</td>
                      <td>₹{s.ratePerMinute}/min</td>
                      <td><span className="fw-semibold text-primary">₹{Number(s.totalDeducted).toFixed(2)}</span></td>
                      <td><span className={`badge light ${st.cls}`}>{st.label}</span></td>
                      <td className="text-muted small">{formatDate(s.startedAt)}</td>
                      <td className="text-muted small">{formatDate(s.endedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="d-sm-flex text-center justify-content-between align-items-center px-3 py-3">
            <div className="dataTables_info">Showing {showingFrom} to {showingTo} of {total} entries</div>
            <div className="dataTables_paginate paging_simple_numbers">
              <Link to="#" className={`paginate_button previous ${page <= 1 ? "disabled" : ""}`}
                onClick={() => page > 1 && setPage(page - 1)}>Previous</Link>
              <span>
                {paggination.map((n) => (
                  <Link key={n} to="#" className={`paginate_button ${page === n ? "current" : ""}`}
                    onClick={() => setPage(n)}>{n}</Link>
                ))}
              </span>
              <Link to="#" className={`paginate_button next ${page >= totalPages ? "disabled" : ""}`}
                onClick={() => page < totalPages && setPage(page + 1)}>Next</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AllSessions;
